<script setup lang="ts">
import { AsyncMarkdownDocument as MarkdownDocument } from '../utils/asyncMarkdownDocument'
import type { UIAlertProps } from '../types'
import type { GlobalNotification, GlobalNotificationPlacement } from '../types/globalNotifications'
import { useGlobalNotifications } from '#imports'
import { proseComponentsFor } from './GlobalNotifications/proseComponents'

export interface GlobalNotificationsProps {
    placement: GlobalNotificationPlacement
}

const props = defineProps<GlobalNotificationsProps>()

const { forPlacement, dismiss } = useGlobalNotifications()
const notifications = forPlacement(() => props.placement)

type AlertType = NonNullable<UIAlertProps['type']>
const severityType: Record<GlobalNotification['severity'], AlertType> = {
    info: 'info',
    warning: 'warning',
    critical: 'danger',
}

const componentsBySeverity = {
    info: proseComponentsFor(severityType.info),
    warning: proseComponentsFor(severityType.warning),
    critical: proseComponentsFor(severityType.critical),
} satisfies Record<GlobalNotification['severity'], unknown>
</script>

<template>
    <UIAlert
        v-for="notification in notifications"
        :key="notification.id"
        is-reminder
        :type="severityType[notification.severity]"
        :closeable="notification.dismissible"
        :data-test="`global-notification-${notification.placement}-${notification.id}`"
        @closed="dismiss(notification.id)"
    >
        <h4 v-if="notification.title">
            {{ notification.title }}
        </h4>
        <MarkdownDocument :value="notification.tree" :components="componentsBySeverity[notification.severity]" />
    </UIAlert>
</template>
